import type { PropsWithChildren, ReactNode } from 'react';
import { useEffect, useRef } from 'react';
import {
  CloseButton,
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from '@headlessui/react';
import Close from '@engineering/icons/close';
import type { OverlaySize, OverlayTitlePosition } from './types';
import {
  Backdrop,
  Wrapper,
  Content,
  TitleWrapper,
  Title,
  ScrollableContent,
  StyledIconButton,
  Footer,
} from './Overlay.styles';

// Checkpoints (ms after mount) sampled to detect a sustained narrow render
// of a full-viewport overlay, rather than a single-frame flash. See WWW-3521.
const NARROW_RENDER_CHECK_DELAYS_MS = [0, 150, 400];
const NARROW_RENDER_WIDTH_RATIO_THRESHOLD = 0.8;
const NARROW_RENDER_MIN_SAMPLES = 2;

type NarrowRenderInfo = {
  renderedWidth: number;
  viewportWidth: number;
};

type Props = PropsWithChildren<{
  id?: string;
  title?: ReactNode;
  titlePosition?: OverlayTitlePosition;
  footerContent?: ReactNode;
  closeLabel?: string;
  open: boolean;
  width?: OverlaySize;
  withoutPadding?: boolean;
  centered?: boolean;
  onClose: () => void;
  /**
   * Called at most once if a full-viewport overlay renders noticeably
   * narrower than the viewport across several checkpoints after mount.
   * Only observed for `width="full"`. See WWW-3521.
   */
  onNarrowRender?: (info: NarrowRenderInfo) => void;
}>;

const Overlay = ({
  id,
  title,
  titlePosition = 'auto',
  footerContent,
  closeLabel = 'close', // you should set this to the correct translation
  children,
  open,
  width = 'normal',
  withoutPadding = false,
  centered = false,
  onClose,
  onNarrowRender,
}: Props) => {
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open || width !== 'full' || !onNarrowRender) return undefined;
    const node = contentRef.current;
    if (!node) return undefined;

    let cancelled = false;
    const samples: NarrowRenderInfo[] = [];

    const timers = NARROW_RENDER_CHECK_DELAYS_MS.map(delay =>
      window.setTimeout(() => {
        if (cancelled) return;

        samples.push({
          renderedWidth: node.getBoundingClientRect().width,
          viewportWidth: window.innerWidth,
        });

        if (samples.length === NARROW_RENDER_CHECK_DELAYS_MS.length) {
          const narrowSamples = samples.filter(
            sample =>
              sample.renderedWidth / sample.viewportWidth <
              NARROW_RENDER_WIDTH_RATIO_THRESHOLD,
          );
          if (narrowSamples.length >= NARROW_RENDER_MIN_SAMPLES) {
            const narrowestSample = narrowSamples.reduce((current, sample) =>
              sample.renderedWidth < current.renderedWidth ? sample : current,
            );
            onNarrowRender(narrowestSample);
          }
        }
      }, delay),
    );

    return () => {
      cancelled = true;
      timers.forEach(timer => window.clearTimeout(timer));
    };
  }, [open, width, onNarrowRender]);

  return (
    <Dialog onClose={onClose} open={open}>
      <DialogBackdrop as={Backdrop} />
      <Wrapper
        role="presentation"
        id={id}
        $width={width}
        $centered={!!centered}
      >
        <DialogPanel>
          <Content
            ref={contentRef}
            role="presentation"
            $fullViewport={width === 'full'}
            $rounded={!!centered}
          >
            <TitleWrapper $hasTitle={!!title}>
              {title && (
                <DialogTitle as="div">
                  <Title $titlePosition={titlePosition}>{title}</Title>
                </DialogTitle>
              )}
              <CloseButton
                as={StyledIconButton}
                icon={Close}
                title={closeLabel}
              />
            </TitleWrapper>
            <ScrollableContent
              role="presentation"
              $fullViewport={width === 'full'}
              $withoutPadding={withoutPadding}
            >
              {children}
            </ScrollableContent>
            {footerContent && (
              <Footer $fullViewport={width === 'full'}>{footerContent}</Footer>
            )}
          </Content>
        </DialogPanel>
      </Wrapper>
    </Dialog>
  );
};

export { Overlay };
